/**
 * AgentSuggestScreen — "Sugestão de Agentes".
 *
 * Você descreve o escopo de negócio/requisitos e escolhe o tipo de entrega
 * (Fullstack / Frontend / Backend / Análise); o LLM analisa e sugere quais
 * agentes dos 394 do catálogo montam o melhor squad para esse resultado.
 *
 * Advisory: a sugestão é para você entender/planejar o squad — não altera
 * automaticamente o pipeline (hoje quem gera são os agentes cabeados; ver o
 * badge "Em uso" no Catálogo).
 */
import { C } from '@/theme/palette';
import { useState, useEffect, useMemo } from 'react';
import { useSuggestAgents, useProjectSteps, useSetProjectSteps } from '@/api/agents';
import { useRecentProjects } from '@/api/dashboard';
import { AiTruncationNotice } from '@/components/ui';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const DELIVERY = [
  { id: 'fullstack', label: 'Fullstack', desc: 'Frontend + Backend + Dados' },
  { id: 'frontend',  label: 'Frontend',  desc: 'UI/UX, cliente web' },
  { id: 'backend',   label: 'Backend',   desc: 'APIs, serviços, persistência' },
  { id: 'analise',   label: 'Análise',   desc: 'Requisitos, arquitetura, riscos (sem gerar app)' },
];

export function AgentSuggestScreen() {
  const [deliveryType, setDeliveryType] = useState('fullstack');
  const [scope, setScope] = useState('');
  const suggest = useSuggestAgents();

  const data = suggest.data;
  const suggested = data?.suggested ?? [];

  // ── Aplicar squad a um projeto (override step→agente) ────────────────────
  const projectsQ = useRecentProjects();
  const projects = Array.isArray(projectsQ.data) ? projectsQ.data : (projectsQ.data?.items ?? []);
  const [applyProjectId, setApplyProjectId] = useState('');
  const stepsQ = useProjectSteps(applyProjectId);
  const setSteps = useSetProjectSteps();
  const [roleAgents, setRoleAgents] = useState({});

  const stepsList = stepsQ.data?.steps ?? [];

  useEffect(() => {
    if (Array.isArray(stepsQ.data?.steps)) {
      const m = {};
      stepsQ.data.steps.forEach((s) => { m[s.step] = s.agent; });
      setRoleAgents(m);
    }
  }, [stepsQ.data]);

  const agentOptions = useMemo(() => {
    const set = new Set();
    stepsList.forEach((s) => { if (s.default_agent) set.add(s.default_agent); });
    suggested.forEach((a) => { if (a.name && a.exists !== false) set.add(a.name); });
    Object.values(roleAgents).forEach((v) => { if (v) set.add(v); });
    return Array.from(set).sort();
  }, [stepsList, suggested, roleAgents]);

  const applySquad = () => {
    if (!applyProjectId) return;
    setSteps.mutate({ projectId: applyProjectId, step_agents: roleAgents });
  };

  const canRun = scope.trim().length >= 10 && !suggest.isPending;

  const run = () => {
    if (!canRun) return;
    suggest.mutate({ delivery_type: deliveryType, scope_text: scope.trim(), max_agents: 8 });
  };

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Sugestão de Agentes</h2>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: 720 }}>
        Descreva o escopo de negócio / requisitos do projeto e escolha o tipo de entrega.
        A IA analisa e sugere quais agentes do catálogo (394) montam o melhor squad.
      </p>

      {/* Tipo de entrega */}
      <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {DELIVERY.map((d) => {
          const on = d.id === deliveryType;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setDeliveryType(d.id)}
              aria-pressed={on}
              style={{
                textAlign: 'left', cursor: 'pointer', minWidth: 150,
                background: on ? `${C.accent}18` : C.surface,
                border: `1px solid ${on ? C.accent : C.border}`,
                borderRadius: 10, padding: '10px 14px', color: C.text,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: on ? C.accent : C.text }}>{d.label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{d.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Escopo */}
      <label htmlFor="scope-input" style={{ display: 'block', marginTop: 20, fontSize: 12.5, color: C.dim }}>
        Escopo de negócio / requisitos
      </label>
      <textarea
        id="scope-input"
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        placeholder="Ex.: Plataforma de e-commerce B2B com catálogo, carrinho, checkout, gestão de pedidos e painel administrativo. Integração com ERP via API. Multi-tenant..."
        rows={6}
        style={{
          width: '100%', marginTop: 6, boxSizing: 'border-box',
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
          color: C.text, fontSize: 13.5, padding: 12, outline: 'none', resize: 'vertical',
          fontFamily: 'inherit', lineHeight: 1.5,
        }}
      />

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          type="button"
          onClick={run}
          disabled={!canRun}
          style={{
            background: canRun ? C.accent : C.surface2,
            color: canRun ? '#0A0A0A' : C.muted,
            border: `1px solid ${canRun ? C.accent : C.border}`,
            borderRadius: 8, padding: '9px 18px', fontSize: 13.5, fontWeight: 600,
            cursor: canRun ? 'pointer' : 'not-allowed',
          }}
        >
          {suggest.isPending ? 'Analisando…' : 'Analisar e sugerir squad'}
        </button>
        {scope.trim().length > 0 && scope.trim().length < 10 && (
          <span style={{ fontSize: 12, color: C.muted }}>Descreva um pouco mais o escopo.</span>
        )}
      </div>

      {suggest.isError && (
        <div style={{ marginTop: 20, padding: 16, border: `1px solid ${C.warn}`, borderRadius: 10, color: C.warn, fontSize: 13 }}>
          Não foi possível gerar a sugestão agora ({suggest.error?.message || 'erro no AI Core'}). Tente de novo.
        </div>
      )}

      {/* Resultado */}
      {data && (
        <div style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Squad sugerido</h3>
            <span style={{ fontSize: 12, color: C.dim }}>
              entrega <strong style={{ color: C.accent }}>{data.delivery_type}</strong> · {suggested.length} agente(s)
            </span>
          </div>
          {data.notes && (
            <p style={{ marginTop: 8, fontSize: 12.5, color: C.dim, fontStyle: 'italic' }}>{data.notes}</p>
          )}

          <AiTruncationNotice complete={data?.aiComplete} onRetry={run} />

          {suggested.length === 0 ? (
            <div style={{ marginTop: 16, padding: 20, border: `1px dashed ${C.border}`, borderRadius: 10, color: C.dim, textAlign: 'center' }}>
              A IA não retornou agentes — tente detalhar mais o escopo.
            </div>
          ) : (
            <div style={{ marginTop: 14, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {suggested.map((a, i) => (
                <div
                  key={`${a.name}-${i}`}
                  style={{
                    background: C.surface, border: `1px solid ${a.in_use ? C.accent : C.border}`,
                    borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{a.name}</span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      {a.in_use && (
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 999, padding: '1px 6px' }}>
                          em uso
                        </span>
                      )}
                      {!a.exists && (
                        <span title="Nome não encontrado no catálogo" style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 999, padding: '1px 6px' }}>
                          ?
                        </span>
                      )}
                    </span>
                  </div>
                  {a.role && <span style={{ fontSize: 12, color: C.accent }}>{a.role}</span>}
                  {a.why && <span style={{ fontSize: 12.5, color: C.text, lineHeight: 1.4 }}>{a.why}</span>}
                  {a.category && <span style={{ fontSize: 10.5, color: C.muted, fontFamily: 'monospace' }}>{a.category}</span>}
                </div>
              ))}
            </div>
          )}

          <p style={{ marginTop: 18, fontSize: 11.5, color: C.muted, maxWidth: 760, lineHeight: 1.5 }}>
            Sugestão consultiva: hoje quem gera código são os agentes cabeados no pipeline (badge
            "Em uso" no Catálogo). Use isto para entender o squad ideal e, se quiser, ajustar
            personas/skills desses agentes no Catálogo.
          </p>
        </div>
      )}

      {/* Aplicar squad a um projeto — ISSO muda a geração de verdade */}
      <div style={{ marginTop: 34, borderTop: `1px solid ${C.border}`, paddingTop: 22 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Aplicar a um projeto</h3>
        <p style={{ color: C.dim, marginTop: 8, fontSize: 13, maxWidth: 720 }}>
          Escolha um projeto e defina qual agente faz cada passo do pipeline. Isto{' '}
          <strong style={{ color: C.text }}>muda a geração desse projeto de verdade</strong>{' '}
          (o worker usa esses agentes na próxima execução). Vazio = agente padrão.
        </p>

        <select
          value={applyProjectId}
          onChange={(e) => setApplyProjectId(e.target.value)}
          aria-label="Projeto"
          style={{
            marginTop: 10, padding: '9px 12px', background: C.surface,
            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
            fontSize: 13.5, minWidth: 280,
          }}
        >
          <option value="">Selecione um projeto…</option>
          {projects.map((p) => {
            const pid = p.id ?? p.project_id ?? p.projectId;
            const pnm = p.name ?? p.project_name ?? p.title ?? pid;
            return <option key={pid} value={pid}>{pnm}</option>;
          })}
        </select>

        {applyProjectId && stepsQ.isLoading && (
          <div style={{ marginTop: 14, color: C.dim }}>Carregando papéis…</div>
        )}

        {applyProjectId && stepsList.length > 0 && (
          <div style={{ marginTop: 16, display: 'grid', gap: 8, maxWidth: 660 }}>
            {stepsList.map((s) => {
              const val = roleAgents[s.step] ?? s.agent;
              const isOverride = val && val !== s.default_agent;
              const opts = agentOptions.includes(val) ? agentOptions : [val, ...agentOptions];
              return (
                <div key={s.step} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.text }}>
                    {s.label}
                    {isOverride && <span style={{ marginLeft: 8, fontSize: 10, color: C.accent }}>alterado</span>}
                  </span>
                  <select
                    value={val}
                    onChange={(e) => setRoleAgents((m) => ({ ...m, [s.step]: e.target.value }))}
                    style={{
                      padding: '7px 10px', background: C.surface,
                      border: `1px solid ${isOverride ? C.accent : C.border}`,
                      borderRadius: 7, color: C.text, fontSize: 12.5,
                    }}
                  >
                    {opts.map((n) => (
                      <option key={n} value={n}>
                        {n}{n === s.default_agent ? ' (padrão)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}

            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                type="button"
                onClick={applySquad}
                disabled={setSteps.isPending}
                style={{
                  background: C.accent, color: '#0A0A0A', border: 'none',
                  borderRadius: 8, padding: '9px 18px', fontSize: 13.5, fontWeight: 600,
                  cursor: setSteps.isPending ? 'wait' : 'pointer',
                }}
              >
                {setSteps.isPending ? 'Aplicando…' : 'Aplicar squad ao projeto'}
              </button>
              {setSteps.isSuccess && (
                <span style={{ fontSize: 12.5, color: C.accent }}>✓ Aplicado — vale na próxima execução.</span>
              )}
              {setSteps.isError && (
                <span style={{ fontSize: 12.5, color: C.warn }}>
                  Falha ao aplicar ({setSteps.error?.message || 'erro'}).
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
